import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ensureUserProfile,
  hydrateSessionFromHash,
  kuunda,
  type Session,
} from '@/lib/kuunda';
import { consumeOAuthReferral, ensureNativeOAuthListener } from '@/features/auth/googleAuth';
import { consumePasswordRecoveryPending } from '@/features/auth/passwordRecovery';
import { findReferrerProfileId } from '@/features/auth/referral';
import '@/pages/marketing/marketing.css';

const OTP_TYPES = new Set<string>(['signup', 'invite', 'magiclink', 'recovery', 'email_change', 'email']);

function clearAuthParamsFromUrl() {
  const url = new URL(window.location.href);
  url.search = '';
  url.hash = '';
  window.history.replaceState({}, document.title, url.pathname);
}

/**
 * Finalise OAuth Google et les liens e-mail (confirmation, reset, magic link).
 * Kuunda ne lit pas le hash `#access_token=` tout seul : hydrater via setSession.
 * verifyOtp pour token / token_hash. Pas d’exchangeCodeForSession (absent du SDK).
 */
async function resolveSession(): Promise<Session | null> {
  await kuunda.auth.initialize();

  const url = new URL(window.location.href);
  const tokenHash = url.searchParams.get('token_hash') ?? url.searchParams.get('token');
  const typeParam = url.searchParams.get('type');

  if (tokenHash && typeParam && OTP_TYPES.has(typeParam)) {
    const email = url.searchParams.get('email') ?? undefined;
    const { data, error } = await kuunda.auth.verifyOtp({
      token: tokenHash,
      type: typeParam === 'magiclink' || typeParam === 'invite' || typeParam === 'email' ? 'email' : (typeParam as 'signup' | 'recovery' | 'email_change'),
      email,
    });
    if (error) throw error;
    if (data.session) return data.session;
  }

  const fromHash = hydrateSessionFromHash();
  if (fromHash) return fromHash;

  for (let i = 0; i < 10; i++) {
    const current = kuunda.auth.getSession();
    if (current) return current;
    await new Promise((r) => setTimeout(r, 150));
  }
  return null;
}

function friendlyAuthError(message: string): string {
  if (/otp_expired|token.*expired|expired|invalid/i.test(message)) {
    return 'Ce lien a expiré ou a déjà été utilisé. Demandez un nouvel e-mail depuis l’inscription ou « Mot de passe oublié ».';
  }
  return message;
}

/**
 * Point de retour OAuth (Google) et confirmation e-mail.
 * Hash access_token → setSession, puis route selon le rôle.
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    ensureNativeOAuthListener();
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const url = new URL(window.location.href);
        const oauthError =
          url.searchParams.get('error_description') || url.searchParams.get('error');
        if (oauthError) {
          if (alive) setError(friendlyAuthError(oauthError));
          return;
        }

        const session = await resolveSession();
        if (!alive) return;

        if (!session?.user) {
          setError('Connexion interrompue. Réessayez depuis la page de connexion.');
          return;
        }

        await ensureUserProfile(session.user);

        const isRecovery =
          consumePasswordRecoveryPending() ||
          url.searchParams.get('type') === 'recovery' ||
          url.searchParams.get('flow') === 'recovery' ||
          /(?:^|&)type=recovery(?:&|$)/i.test(window.location.hash.replace(/^#/, ''));

        clearAuthParamsFromUrl();

        if (isRecovery) {
          navigate('/reinitialiser-mot-de-passe', { replace: true });
          return;
        }

        const referral = consumeOAuthReferral();
        if (referral) {
          const parrainId = await findReferrerProfileId(referral);
          if (parrainId && parrainId !== session.user.id) {
            await kuunda
              .from('profiles')
              .update({ referred_by: parrainId })
              .eq('id', session.user.id)
              .is('referred_by', null);
          }
        }

        let role: string | undefined;
        for (let i = 0; i < 10; i++) {
          const { data: profile } = await kuunda
            .from('profiles')
            .select('role')
            .eq('id', session.user.id)
            .maybeSingle();
          if (profile) {
            role = (profile as { role?: string }).role;
            break;
          }
          await new Promise((r) => setTimeout(r, 200));
        }

        if (!alive) return;
        navigate(role === 'admin' || role === 'advisor' ? '/admin' : '/app', { replace: true });
      } catch (err) {
        if (!alive) return;
        setError(
          friendlyAuthError(err instanceof Error ? err.message : 'Échec de la finalisation de connexion'),
        );
      }
    })();

    return () => {
      alive = false;
    };
  }, [navigate]);

  if (error) {
    return (
      <div className="page-center">
        <h1>Connexion</h1>
        <p className="error-banner">{error}</p>
        <p className="stack-gap">
          <Link className="btn btn-primary" to="/connexion">
            Retour à la connexion
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="page-center">
      <p className="muted">Finalisation de la connexion…</p>
    </div>
  );
}
